import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const mapNotification = (notification) => ({
  ...notification,
  created_at: notification.created_at,
  target: notification.target || null,
  read: Boolean(notification.read_at)
});

const useNotifications = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const inFlightListRequestRef = useRef(null);

  const loadUnreadCount = useCallback(async () => {
    if (!user) {
      setUnreadCount(0);
      return;
    }
    try {
      const response = await api.get('/notifications/unread-count');
      setUnreadCount(response.data?.unread_count || 0);
    } catch (error) {
      console.warn('Failed to fetch unread notifications count', error);
    }
  }, [user]);

  const fetchNotifications = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    if (inFlightListRequestRef.current) {
      return inFlightListRequestRef.current;
    }

    try {
      setLoading(true);
      const request = api.get('/notifications', { params: { limit: 20 } });
      inFlightListRequestRef.current = request;
      const response = await request;

      const items = response.data?.notifications || [];
      const mappedItems = items.map(mapNotification);
      setNotifications(mappedItems);

      // Keep badge driven by /unread-count (source of truth), but refresh it after
      // list fetch since the backend may have marked invalid notifications as read.
      loadUnreadCount();
    } catch (error) {
      console.warn('Failed to fetch notifications', error);
      setNotifications([]);
    } finally {
      setLoading(false);
      inFlightListRequestRef.current = null;
    }
  }, [loadUnreadCount, user]);

  useEffect(() => {
    loadUnreadCount();
  }, [loadUnreadCount]);

  useEffect(() => {
    if (user) return;
    setNotifications([]);
    setUnreadCount(0);
    inFlightListRequestRef.current = null;
  }, [user]);

  const markAllRead = useCallback(async () => {
    if (!user) return;
    // Optimistic update: badge clears immediately.
    setNotifications((prev) =>
      prev.map((item) => ({
        ...item,
        read: true,
        read_at: item.read_at || new Date().toISOString()
      }))
    );
    setUnreadCount(0);
    try {
      await api.post('/notifications/mark-all-read');
    } catch (error) {
      console.warn('Failed to mark notifications as read', error);
      // Best-effort resync if the optimistic update failed server-side.
      loadUnreadCount();
    }
  }, [loadUnreadCount, user]);

  const markOneRead = useCallback(async (notificationId) => {
    if (!user) return;
    let wasUnread = false;
    setNotifications((prev) =>
      prev.map((item) => {
        if (item.id !== notificationId) return item;
        if (!item.read) {
          wasUnread = true;
        }
        return { ...item, read: true, read_at: item.read_at || new Date().toISOString() };
      })
    );
    if (wasUnread) {
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }
    try {
      await api.post(`/notifications/${notificationId}/mark-read`);
    } catch (error) {
      console.warn('Failed to mark notification as read', error);
    }
  }, [user]);

  return {
    notifications,
    unreadCount,
    loading,
    refresh: fetchNotifications,
    markAllRead,
    markOneRead
  };
};

export default useNotifications;


